/**
 * Community Chat API — Supabase PostgREST + RPC + Realtime.
 *
 * Implements the weekly-topic community chat (REQUIREMENTS §3.5):
 *   • Topic nomination + weekly voting
 *   • The single active weekly group chat (rotating animal pseudonyms)
 *   • Silent flag-to-mute shadowban + admin oversight
 *
 * Anonymity: message reads deliberately omit `user_id` for residents so the
 * pseudonym → person mapping never reaches the client. Bubble alignment is done
 * by comparing each message's pseudonym to the caller's own weekly pseudonym.
 * Shadowban / hard-hide visibility is enforced server-side by RLS (migration
 * 008), so Realtime inherits it automatically.
 */

import { profanityFilter as filter } from "../utils/profanity";
import supabase from "../config/supabase";

// Columns safe to expose to residents (no user_id → preserves anonymity).
const RESIDENT_MESSAGE_SELECT =
  "id, topic_id, pseudonym, content, is_hidden, flag_count, is_shadowbanned, created_at";

// Moderators additionally need the author + linkage to act on abuse.
const MOD_MESSAGE_SELECT =
  "id, topic_id, user_id, pseudonym, content, is_hidden, hidden_by, flag_count, is_shadowbanned, created_at, author:users!user_id(id, first_name, last_name)";

const communityChatApi = {
  /**
   * Topics open for nomination/voting this week, sorted by votes (desc).
   * Each topic is annotated with `my_vote` so the UI can show vote state.
   */
  async listTopics() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("community_topics")
      .select("*, submitter:users!submitted_by(first_name, last_name)")
      .eq("status", "nominating")
      .order("vote_count", { ascending: false })
      .order("created_at", { ascending: true });

    if (error) return { success: false, error: error.message };

    const { data: myVotes } = await supabase
      .from("community_topic_votes")
      .select("topic_id")
      .eq("user_id", user.id);
    const votedSet = new Set((myVotes || []).map((v) => v.topic_id));

    const topics = (data || []).map((t) => ({
      ...t,
      my_vote: votedSet.has(t.id),
    }));
    return { success: true, data: topics };
  },

  /** The current active weekly chat topic, or null if none is running yet. */
  async getActiveTopic() {
    const { data, error } = await supabase
      .from("community_topics")
      .select("*")
      .eq("status", "active")
      .order("week_start", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || null };
  },

  /** Recently closed weekly chats (read-only archive). */
  async listClosedTopics(limit = 10) {
    const { data, error } = await supabase
      .from("community_topics")
      .select("*")
      .eq("status", "closed")
      .order("week_start", { ascending: false })
      .limit(limit);

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  /** Submit a new topic for voting (≤140 chars, bad-words filtered). */
  async nominateTopic(title) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const clean = (title || "").trim();
    if (clean.length < 5) {
      return { success: false, error: "Topic must be at least 5 characters." };
    }
    if (clean.length > 140) {
      return { success: false, error: "Topic must be 140 characters or fewer." };
    }
    if (filter.isProfane(clean)) {
      return { success: false, error: "Please reword — that contains language we can't post." };
    }

    const { data, error } = await supabase
      .from("community_topics")
      .insert({ title: clean, submitted_by: user.id, status: "nominating" })
      .select("*, submitter:users!submitted_by(first_name, last_name)")
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: { ...data, my_vote: false } };
  },

  /** Toggle the caller's vote on a topic. Returns the new vote_count. */
  async toggleVote(topicId) {
    const { data, error } = await supabase.rpc("community_toggle_vote", {
      p_topic_id: topicId,
    });
    if (error) return { success: false, error: error.message };
    return { success: true, data: { vote_count: data } };
  },

  /**
   * Messages for a topic. Residents get anonymised rows; moderators get the
   * full stream incl. hidden/shadowbanned plus author identity.
   */
  async listMessages(topicId, { asModerator = false } = {}) {
    const { data, error } = await supabase
      .from("community_chat_messages")
      .select(asModerator ? MOD_MESSAGE_SELECT : RESIDENT_MESSAGE_SELECT)
      .eq("topic_id", topicId)
      .order("created_at", { ascending: true });

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  /** The caller's own pseudonym for a week (for own-message bubble alignment). */
  async getMyPseudonym(weekStart) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: true, data: null };

    let query = supabase
      .from("community_pseudonyms")
      .select("pseudonym, week_start")
      .eq("user_id", user.id);
    if (weekStart) query = query.eq("week_start", weekStart);

    const { data, error } = await query
      .order("week_start", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data ? data.pseudonym : null };
  },

  /** Post a message to the active topic (bad-words filtered). */
  async sendMessage(topicId, content) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const clean = (content || "").trim();
    if (!clean) return { success: false, error: "Message is empty." };
    if (filter.isProfane(clean)) {
      return { success: false, error: "Please reword — that contains language we can't post." };
    }

    // pseudonym / user_id / shadowban are filled by the BEFORE INSERT trigger.
    const { data, error } = await supabase
      .from("community_chat_messages")
      .insert({ topic_id: topicId, content: clean, pseudonym: "" })
      .select(RESIDENT_MESSAGE_SELECT)
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  /** Flag another resident's message (3 unique flaggers → author shadowbanned). */
  async flagMessage(messageId) {
    const { error } = await supabase.rpc("community_flag_message", {
      p_message_id: messageId,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /** Admin: hard-hide (thumbs-down) or restore a message. */
  async adminSetHidden(messageId, hidden) {
    const { error } = await supabase.rpc("community_admin_set_hidden", {
      p_message_id: messageId,
      p_hidden: hidden,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /** Admin: manually run the weekly rotation now. Returns new active topic id. */
  async rotateWeek() {
    const { data, error } = await supabase.rpc("community_rotate_week");
    if (error) return { success: false, error: error.message };
    return { success: true, data: { active_topic_id: data } };
  },

  /**
   * Subscribe to live message changes for a topic. RLS gates which rows each
   * client receives (shadowbanned/hidden messages won't reach other residents).
   * Returns the channel — call communityChatApi.unsubscribe(channel) to clean up.
   */
  subscribeToTopic(topicId, { onInsert, onUpdate } = {}) {
    const channel = supabase
      .channel(`community-chat:${topicId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "community_chat_messages",
          filter: `topic_id=eq.${topicId}`,
        },
        (payload) => onInsert && onInsert(payload.new),
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "community_chat_messages",
          filter: `topic_id=eq.${topicId}`,
        },
        (payload) => onUpdate && onUpdate(payload.new),
      )
      .subscribe();
    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase.removeChannel(channel);
  },
};

export default communityChatApi;
